import classnames from 'clsx';
import flow from 'lodash/flow';
import PropTypes from 'prop-types';
import { Component } from 'react';
import { player } from 'src/client/player';
import connectWithPlayer from 'src/common/decorators/connectWithPlayer';
import withNowPlayingContext from '../../../decorators/withNowPlayingContext';
import GuideItemLink from './GuideItemLink';
import css from './guide-item.module.scss';
import LockedTile from './LockedTile';

export class GuideItemTitle extends Component {
  static propTypes = {
    children: PropTypes.node,
    guideItem: PropTypes.object.isRequired,
    style: PropTypes.object,
    syncedPlayerStatus: PropTypes.string.isRequired,
    syncedPlayerAction: PropTypes.func.isRequired,
    isSyncedWithPlayer: PropTypes.bool.isRequired,
    getIsNowPlayingOpen: PropTypes.func.isRequired,
    nowPlayingContext: PropTypes.object.isRequired,
    selectedContentId: PropTypes.string,
    canScrub: PropTypes.bool,
    hasImage: PropTypes.bool,
    hasOwnTitleWrapper: PropTypes.bool,
    isClickHandlerEnabled: PropTypes.bool,
    className: PropTypes.string,
  };

  handleGuideItemLinkClick = (to, event) => {
    const {
      guideItem,
      playerActions,
      syncedPlayerAction,
      isSyncedWithPlayer,
      getIsNowPlayingOpen,
      canScrub,
      nowPlayingContext,
    } = this.props;
    const isNowPlayingOpen = getIsNowPlayingOpen();

    if (!isNowPlayingOpen) {
      // we want to block navigation when nowPlaying is NOT open
      // because this function is only passed to GuideItemLink below
      // when the item is playable (i.e. topic/episode) or when in nowPlaying
      // and shouldNavigate returns true
      event.preventDefault();
    }

    // when in nowplaying and currently playing item is clicked
    // we want to only scroll and NOT control playback or retune
    if (isSyncedWithPlayer && isNowPlayingOpen) {
      nowPlayingContext.scrollToTop();
      return;
    }

    if (isSyncedWithPlayer) {
      syncedPlayerAction(playerActions, canScrub);
    } else {
      player.onClick(guideItem);

      if (isNowPlayingOpen) {
        nowPlayingContext.scrollToTop();
      }
    }
  };

  // Links that navigate are only intercepted while Now Playing is open; otherwise they navigate.
  handleNavigatingLinkClick = (to, event) => {
    if (this.props.getIsNowPlayingOpen()) {
      this.handleGuideItemLinkClick(to, event);
    }
  };

  render() {
    const {
      guideItem,
      children,
      selectedContentId,
      hasImage,
      hasOwnTitleWrapper,
      isClickHandlerEnabled,
      className,
    } = this.props;

    if (!children) {
      return null;
    }

    const titlesClass = classnames(className, {
      [css.guideItemTitlesContainer]: !hasOwnTitleWrapper,
      [css.guideItemTitlesContainerNoImage]: !hasImage,
    });

    /* No anchor */
    if (!guideItem.actionName || guideItem.isSelectable) {
      return <div className={titlesClass}>{children}</div>;
    }

    /* If should show the upsell dialog */
    if (guideItem.isLocked) {
      return (
        <LockedTile
          dataTestId="lockedTitle"
          guideItem={guideItem}
          className={titlesClass}
          isClickHandlerEnabled={isClickHandlerEnabled}
        >
          {children}
        </LockedTile>
      );
    }

    /* If should link to a profile or another browse page */
    if (guideItem.shouldNavigate && guideItem?.action?.destinationInfo?.id !== selectedContentId) {
      return (
        <GuideItemLink
          dataTestId="guideItemTitleLink"
          guideItem={guideItem}
          className={titlesClass}
          onClick={this.handleNavigatingLinkClick}
        >
          {children}
        </GuideItemLink>
      );
    }

    /* If it's playable then anchor so tune begins on click */
    if (guideItem.isPlayable) {
      return (
        <GuideItemLink
          dataTestId="guideItemTitleLink"
          onClick={this.handleGuideItemLinkClick}
          guideItem={guideItem}
          className={titlesClass}
        >
          {children}
        </GuideItemLink>
      );
    }

    /* Default anchor to browse or profile */
    return (
      <GuideItemLink dataTestId="guideItemTitleLink" guideItem={guideItem} className={titlesClass}>
        {children}
      </GuideItemLink>
    );
  }
}

export default flow(connectWithPlayer, withNowPlayingContext)(GuideItemTitle);
